const timestamp = value => {
  const micros = (value?.match(/\.(\d+)/)?.[1] || '').padEnd(6, '0');
  return new Date(value).getTime() * 1000 + Number(micros.slice(3, 6));
};

export const ownChannelGeneration = (state, channelId) => {
  const generation = state.channelRemovalGeneration[channelId] || 0;
  return () =>
    generation === (state.channelRemovalGeneration[channelId] || 0) &&
    !state.removedChannels[channelId];
};

export const sortByCreatedAt = (left, right) =>
  timestamp(left.created_at) - timestamp(right.created_at) ||
  left.id - right.id;

export const upsertById = (records, value) => {
  if (!records.some(record => record.id === value.id))
    return [...records, value];
  return records.map(record =>
    record.id === value.id ? { ...record, ...value } : record
  );
};

export const mergeMessageRevision = (current, incoming) => {
  if (!current) return incoming;
  if (
    (current.deleted && !incoming.deleted) ||
    timestamp(current.updated_at) >= timestamp(incoming.updated_at)
  )
    return current;
  return {
    ...current,
    ...incoming,
    // Editing message content does not change reaction identity or freshness.
    reactions: current.reactions ?? incoming.reactions,
    reactionRemovals: current.reactionRemovals,
  };
};

export const upsertMessageById = (records, incoming) => {
  const current = records.find(record => record.id === incoming.id);
  const merged = mergeMessageRevision(current, incoming);
  return merged === current ? records : upsertById(records, merged);
};

export const applyReaction = (message, { messageId, reaction, removed }) => {
  if (message.id !== messageId) return message;
  const key = `${reaction.user_id}:${reaction.emoji}`;
  const previousRemoval = message.reactionRemovals?.[key];
  if (!removed && previousRemoval?.id >= reaction.id) return message;
  const reactions = message.reactions || [];
  const current = reactions.find(item => item.emoji === reaction.emoji);
  const index = current?.user_ids.indexOf(reaction.user_id) ?? -1;
  if (removed) {
    const removal =
      previousRemoval?.id > reaction.id ? previousRemoval : reaction;
    const updated = {
      ...message,
      reactionRemovals: { ...message.reactionRemovals, [key]: removal },
    };
    if (index < 0 || current.reaction_ids[index] > removal.id) return updated;
    return {
      ...updated,
      reactions: reactions
        .map(item =>
          item === current
            ? {
                ...item,
                count: item.count - 1,
                user_ids: item.user_ids.filter((_, i) => i !== index),
                reaction_ids: item.reaction_ids.filter((_, i) => i !== index),
              }
            : item
        )
        .filter(item => item.count > 0),
    };
  }
  if (index >= 0) {
    if (current.reaction_ids[index] >= reaction.id) return message;
    return {
      ...message,
      reactions: reactions.map(item =>
        item === current
          ? {
              ...item,
              reaction_ids: item.reaction_ids.map((id, position) =>
                position === index ? reaction.id : id
              ),
            }
          : item
      ),
    };
  }
  const updated = current
    ? {
        ...current,
        count: current.count + 1,
        user_ids: [...current.user_ids, reaction.user_id],
        reaction_ids: [...current.reaction_ids, reaction.id],
      }
    : {
        emoji: reaction.emoji,
        count: 1,
        user_ids: [reaction.user_id],
        reaction_ids: [reaction.id],
      };
  return {
    ...message,
    reactions: current
      ? reactions.map(item => (item === current ? updated : item))
      : [...reactions, updated],
  };
};

export const preserveReactionRemovals = (current, incoming) =>
  Object.values(current?.reactionRemovals || {}).reduce(
    (message, reaction) =>
      applyReaction(message, {
        messageId: incoming.id,
        reaction,
        removed: true,
      }),
    incoming
  );

export const withoutDeletedPreview = (state, notification) =>
  state.messageTombstones[notification.message_id]
    ? { ...notification, message_preview: null }
    : notification;

export const reconcileMessage = (state, incoming) => {
  const message = mergeMessageRevision(
    state.messageTombstones[incoming.id],
    incoming
  );
  if (message.deleted) {
    state.messageTombstones[message.id] = message;
    state.searchResults = state.searchResults.filter(
      record => record.id !== message.id
    );
    ['notifications', 'pendingNotifications'].forEach(key => {
      state[key] = state[key].map(notification =>
        withoutDeletedPreview(state, notification)
      );
    });
  }
  const records = state.messages[message.channel_id];
  const reconcile = record =>
    record.id === message.id ? mergeMessageRevision(record, message) : record;
  if (message.deleted) {
    if (records)
      state.messages = {
        ...state.messages,
        [message.channel_id]: records.map(reconcile),
      };
    if (state.thread)
      state.thread = {
        ...state.thread,
        parent: reconcile(state.thread.parent),
        replies: state.thread.replies.map(reconcile),
      };
  }
  return preserveReactionRemovals(
    { reactionRemovals: state.reactionRemovals[message.id] },
    message
  );
};

// Every asynchronous action belongs to the account generation that started it.
// A reset or account switch prevents its follow-up requests and stale commits.
export const scopedActions = actions =>
  Object.fromEntries(
    Object.entries(actions).map(([name, action]) => [
      name,
      (context, payload) => {
        const generation = context.state.contextGeneration;
        const accountId = context.rootGetters.getCurrentAccountId;
        const current = () =>
          generation === context.state.contextGeneration &&
          accountId === context.rootGetters.getCurrentAccountId;
        return action(
          {
            ...context,
            commit: (...args) => current() && context.commit(...args),
            dispatch: (...args) => current() && context.dispatch(...args),
          },
          payload
        );
      },
    ])
  );
